import { IDestroyOptions, Point, Sprite, Text, TextStyle } from "pixi.js";
import { IPreloaderController } from "../controllers/IPreloaderController";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { PreloaderResourcePackage } from "../resources/PreloaderResourcePackage";
import { ProgressBar } from '@pixi/ui';
import { IPreloaderModel } from "../models/IPreloaderModel";
import { nameof } from "bindable-data";
import { gsap } from "gsap";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../common/GameFont";
import { DropShadowFilter } from "pixi-filters";
import { BaseScalableView } from "../../common/BaseScalableView";
import { GameInfo, Stage } from "../../../../analytics/GameInfo";
import { IApplicationModel } from "../../common/models/IApplicationModel";
type TimeLine = gsap.core.Timeline;

export class HomeScreenPreloaderView extends BaseScalableView<void> {
    private static readonly LOADING_TEXT = "LOADING";
    private static readonly DICE_DEFAULT_ROTATION_ANGLE = -13.21;

    private readonly preloaderResources: PreloaderResourcePackage;
    private readonly controller: IPreloaderController;
    private readonly gameInfo: GameInfo;
    private readonly applicationModel: IApplicationModel;
    private preloaderModel: IPreloaderModel;
    private background: Sprite;
    private logoIcon: Sprite;
    private dice: Sprite;
    private diceAnimation: TimeLine;
    private diceShadowFilter: DropShadowFilter;
    private progressBar: ProgressBar;
    private progressBarShadowFilter: DropShadowFilter;
    private progressValue: Text;
    private gameSceneResolved: PromiseCompletionSource<void>;
    private resourcesPreloaded: PromiseCompletionSource<void>;
    private hideProgressTimeLine: TimeLine;
    private buildNumber: Text;

    public constructor(
        preloaderResources: PreloaderResourcePackage,
        controller: IPreloaderController,
        preloaderModel: IPreloaderModel,
        applicationModel: IApplicationModel,
        canvasScaler: ICanvasScaler,
        gameInfo: GameInfo,
        resourcesPreloaded: PromiseCompletionSource<void>,
        gameSceneResolved: PromiseCompletionSource<void>,
        readyToShowGameScene: PromiseCompletionSource<void>
    ) {
        super(readyToShowGameScene, canvasScaler);
        this.preloaderResources = preloaderResources;
        this.resourcesPreloaded = resourcesPreloaded;
        this.controller = controller;
        this.preloaderModel = preloaderModel;
        this.applicationModel = applicationModel;
        this.gameInfo = gameInfo;
        this.gameSceneResolved = gameSceneResolved;
        this.diceAnimation = gsap.timeline({ repeat: -1 });
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.preloaderModel.propertyChanged.add(this.onPreloaderModelPropertyChanged, this);
    }

    private async onPreloaderModelPropertyChanged(model: IPreloaderModel, key: string): Promise<void> {
        if (key === nameof(model, "progress")) {
            this.progressBar.progress = model.progress.value * 95;
            this.progressValue.text = `${HomeScreenPreloaderView.LOADING_TEXT} ${Math.round(model.progress.value * 100)}%`;
            const doneLoading = model.progress.value === 1;
            if (doneLoading) {
                await this.controller.checkAndUpdateAuthState();
                this.progressBar.progress = 100;
                await this.hideProgressAnimation();
                this.resourcesPreloaded.complete();
                await this.gameSceneResolved.result;
                this.applicationModel.gameLoadedHandle();
                this.operationControls.complete();
            }
        }
    }

    private build(): void {
        this.background = new Sprite(this.preloaderResources.assets_views_preloader_ui_homescreen_background_png);
        this.background.anchor.set(0.5);
        this.addChild(this.background);

        this.logoIcon = new Sprite(this.preloaderResources.assets_views_preloader_ui_homescreen_logo_png);
        this.logoIcon.anchor.set(0.5, 0);
        this.addChild(this.logoIcon);

        this.dice = new Sprite(this.preloaderResources.assets_views_preloader_ui_homescreen_dice_png);
        this.dice.angle = HomeScreenPreloaderView.DICE_DEFAULT_ROTATION_ANGLE;
        this.dice.anchor.set(0.5);
        this.dice.scale.set(0.9);
        this.addChild(this.dice);
        this.diceShadowFilter = new DropShadowFilter({
            offset: new Point(27, 23),
            blur: 21,
            color: 0x0D0606,
            quality: 4.2,
            alpha: 0.8
        });
        this.dice.filters = [this.diceShadowFilter];

        const progressBarBg = new Sprite(this.preloaderResources.assets_views_preloader_ui_homescreen_button_black_png);
        const progressBarFill = new Sprite(this.preloaderResources.assets_views_preloader_ui_homescreen_button_green_png);
        this.progressBar = new ProgressBar({ bg: progressBarBg, fill: progressBarFill, progress: this.preloaderModel.progress.value });
        this.progressBar.pivot.set(this.progressBar.width / 2, this.progressBar.height / 2);
        this.addChild(this.progressBar);

        this.progressValue = new Text("", new ProgressValueTextStyle());
        this.progressValue.anchor.set(0.5);
        this.progressBar.innerView.addChild(this.progressValue);

        this.progressBarShadowFilter = new DropShadowFilter({
            offset: new Point(3, 3),
            blur: 50,
            color: 0x0D0606,
            quality: 4.2,
            alpha: 1
        });
        this.progressBar.filters = [this.progressBarShadowFilter];

        if (this.gameInfo.debug && this.gameInfo.stage !== Stage.LOCALHOST) {
            this.buildNumber = new Text(`build number: ${this.gameInfo.buildNumber}`, new GameVersionTextStyle());
            this.addChild(this.buildNumber);
        }
    }

    private async hideProgressAnimation(): Promise<void> {
        this.hideProgressTimeLine = new gsap.core.Timeline();
        this.hideProgressTimeLine.to(this.progressValue.scale, { x: 0, y: 0, duration: 0.5, ease: "back.in(1)" });
        await this.hideProgressTimeLine.play();
        this.hideProgressTimeLine.kill();
    }

    public start(): void {
        this.startDiceAnimation();
        this.controller.initializeGame();
    }

    private startDiceAnimation(): void {
        this.diceAnimation.to(
            this.dice,
            {
                angle: - 40,
                duration: 0.5,
                ease: "power1.in"
            },
        );
        this.diceAnimation.to(
            this.dice,
            {
                angle: 720 - HomeScreenPreloaderView.DICE_DEFAULT_ROTATION_ANGLE * 2 - 40,
                duration: 1.3,
                ease: "power3.Out"
            },
        );
    }

    private layout(width: number, height: number): void {
        this.background.width = width;
        this.background.height = height;
        this.background.position.set(width * 0.5, height * 0.5);
        this.logoIcon.position.set(width * 0.5, height * 0.007);
        this.dice.position.set(width * 0.5, height * 0.5);
        this.progressBar.position.set(width * 0.5, height * 0.852);
        this.progressValue.position.set(this.progressBar.width / 2, 82);
        if (this.buildNumber != null) {
            this.buildNumber.position.set(width - this.buildNumber.width - 50, height - this.buildNumber.height - 50);
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.preloaderModel.propertyChanged.remove(this.onPreloaderModelPropertyChanged, this);
        this.background.off("touchstart");
        this.hideProgressTimeLine?.kill();
        this.diceAnimation?.kill();
        super.destroy(options);
    }
}

class ProgressValueTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.7,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 72,
            miterLimit: 0
        }));
    }
}

class GameVersionTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: 0xffffff,
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 35
        }));
    }
}